import { createContext, Dispatch, SetStateAction, useContext, useMemo, useState } from 'react';

import { TQTabView } from 'components/TQDataGrid/utils/grid-types';
import { useQueryParams } from 'hooks/useQueryParams';

export enum TQRouteQueryParams {
  THREAD_ID = 'thread_id',
  TAB_VIEW = 'tab_view',
  TERRITORY_ID = 'territory_id',
  SHOW_SETTINGS = 'show_settings',
  TERRITORY_VIEW_ID = 'territory_view_id',
}

export type TQRouteContextType = {
  threadIdQueryParam: string | null;
  tabViewQueryParam: string | null;
  territoryIdQueryParam: string | null;
  showSettingsQueryParam: string | null;
  territoryViewIdQueryParam: string | null;
  areQueryParamsInitialized: Record<TQRouteQueryParams, boolean>;
  setAreQueryParamsInitialized: Dispatch<SetStateAction<Record<TQRouteQueryParams, boolean>>>;
};

const TQRouteContext = createContext<TQRouteContextType>({} as TQRouteContextType);
export const useTQRouteContext = () => useContext(TQRouteContext);

const isValidUUID = (uuid: string): boolean => {
  const uuidPattern = /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[1-5][a-fA-F0-9]{3}-[89abAB][a-fA-F0-9]{3}-[a-fA-F0-9]{12}$/;
  return uuidPattern.test(uuid);
};

const isValidTabView = (tabView: string): tabView is TQTabView => {
  return Object.values(TQTabView).includes(tabView as TQTabView);
};

export const TQRouteContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getQueryParam } = useQueryParams();

  // This is just used to track if the query params have been initialized or consumed if needed
  const [areQueryParamsInitialized, setAreQueryParamsInitialized] = useState<Record<TQRouteQueryParams, boolean>>({
    [TQRouteQueryParams.THREAD_ID]: false,
    [TQRouteQueryParams.TAB_VIEW]: false,
    [TQRouteQueryParams.TERRITORY_ID]: false,
    [TQRouteQueryParams.SHOW_SETTINGS]: false,
    [TQRouteQueryParams.TERRITORY_VIEW_ID]: false,
  });

  // UUID for the thread to display
  let threadIdQueryParam = getQueryParam(TQRouteQueryParams.THREAD_ID);
  // The tab view to display
  let tabViewQueryParam = getQueryParam(TQRouteQueryParams.TAB_VIEW);
  // The territory id to filter on
  let territoryIdQueryParam = getQueryParam(TQRouteQueryParams.TERRITORY_ID);
  // Whether to show the settings drawer
  let showSettingsQueryParam = getQueryParam(TQRouteQueryParams.SHOW_SETTINGS);
  // The territory view id to filter on
  let territoryViewIdQueryParam = getQueryParam(TQRouteQueryParams.TERRITORY_VIEW_ID);

  if (showSettingsQueryParam && showSettingsQueryParam !== 'true') {
    showSettingsQueryParam = null;
  }

  // Validate the query params
  if (threadIdQueryParam && !isValidUUID(threadIdQueryParam)) {
    threadIdQueryParam = null;
  }

  if (tabViewQueryParam && !isValidTabView(tabViewQueryParam)) {
    tabViewQueryParam = null;
  }

  if (territoryIdQueryParam && !isValidUUID(territoryIdQueryParam)) {
    territoryIdQueryParam = null;
  }

  if (territoryViewIdQueryParam && !isValidUUID(territoryViewIdQueryParam)) {
    territoryViewIdQueryParam = null;
  }

  const contextValue = useMemo(
    () => ({
      threadIdQueryParam,
      tabViewQueryParam,
      territoryIdQueryParam,
      areQueryParamsInitialized,
      setAreQueryParamsInitialized,
      showSettingsQueryParam,
      territoryViewIdQueryParam,
    }),
    [
      threadIdQueryParam,
      tabViewQueryParam,
      territoryIdQueryParam,
      areQueryParamsInitialized,
      setAreQueryParamsInitialized,
      showSettingsQueryParam,
      territoryViewIdQueryParam,
    ],
  );

  return <TQRouteContext.Provider value={contextValue}>{children}</TQRouteContext.Provider>;
};
